import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiPencil, HiTrash, HiShieldCheck } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import './RoleIndex.scss';

interface Role {
  id: number;
  name: string;
  description: string;
  permissions: string[];
  isActive: boolean;
  level: string;
  createdAt: string;
  updatedAt: string;
}

export const RoleIndex: React.FC = () => {
  const { t, i18n } = useTranslation(['roles', 'common']);
  const navigate = useNavigate();
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    });

    return params.toString();
  };

  const fetchRoles = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/role?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('roles:messages.fetchError'));
        return;
      }

      setRoles(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error) {
      showErrorToast(t('roles:messages.fetchError'));
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles(currentPage, pageSize);
  }, [currentPage, pageSize, filters]);

  const deleteRole = async (id: number) => {
    if (!window.confirm(t('roles:messages.deleteConfirm'))) return;

    try {
      const response = await adminApi.call(`/role/${id}`, 'DELETE');

      if (!response.success) {
        showErrorToast(response.error || t('roles:messages.deleteError'));
        return;
      }

      showSuccessToast(t('roles:messages.deleteSuccess'));
      fetchRoles(currentPage, pageSize);
    } catch (error) {
      showErrorToast(t('roles:messages.deleteError'));
      console.error('Error deleting role:', error);
    }
  };

  const handleCreateRole = () => {
    navigate('/roles/create');
  };

  const handleEditRole = (id: number) => {
    navigate(`/roles/edit/${id}`);
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ?
      <span className="badge bg-success">{t('roles:statusOptions.active')}</span> :
      <span className="badge bg-secondary">{t('roles:statusOptions.inactive')}</span>;
  };

  const columns: Column[] = [
    {
      key: 'name',
      title: t('roles:table.name'),
      sortable: true,
      filterable: true,
      filterKey: 'name',
    },
    {
      key: 'actions',
      title: t('roles:table.actions'),
      sortable: false,
      width: '150px',
      align: 'center',
      render: (_, role) => (
        <div className="action-buttons">
          {hasPermission("Update Role") && (
            <ActionButton
              variant="warning"
              size="sm"
              icon={HiPencil}
              onClick={() => handleEditRole(role.id)}
              tooltip={t('roles:editRole')}
            />
          )}
          {hasPermission("Delete Role") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deleteRole(role.id)}
              tooltip={t('roles:deleteRole')}
            />
          )}
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    { label: t('roles:breadcrumbs.roles') }
  ];

  return (
    <PageLayout
      title={t('roles:title')}
      subtitle={t('roles:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={roles}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={false}
        refreshable={true}
        addButton={hasPermission("Create Role") ? {
          label: t('roles:addNew'),
          onClick: handleCreateRole
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchRoles(currentPage, pageSize)}
        emptyMessage={t('roles:messages.noRoles')}
      />
    </PageLayout>
  );
};
